import * as A from 'astronomy-engine';
import type { AppServices } from '../app';
import { eqjToEnuMatrix } from '../astro/frame';
import { altAzToEnu, mat3Apply, norm, raDecToVec, type Mat3, type Vec3 } from '../lib/math';
import { keyToSelection, sightingName } from '../live/nights';
import { type Night, type Sighting, nightMoment } from '../live/nightsCore';
import { crescentDay } from '../live/saudi';
import { CRESCENT_LEAST } from './MoonGlyph';

// A night's picture: the whole sky over the place at one moment, drawn as a round chart with north at the
// top and east on the left (as when lying back and looking up), and everything seen that night ringed in
// gold where it was. Drawn in 2D from the app's own catalogue, so it needs no connection and costs little.

export interface ChartOptions {
  /** Diameter in CSS pixels. */
  size: number;
  dpr?: number;
  /** Faintest stars drawn (magnitude). */
  magLimit?: number;
  labels?: boolean;
  lines?: boolean;
}

const GOLD = '#efcf8f';
const PLANETS: [A.Body, string, number][] = [
  [A.Body.Venus, '#fff3c4', 1.9], [A.Body.Jupiter, '#f6e2bd', 1.7], [A.Body.Mars, '#ff9a6b', 1.4], [A.Body.Saturn, '#ecd494', 1.4],
  [A.Body.Mercury, '#d9d2c5', 1.1], [A.Body.Uranus, '#bfe6e8', 0.9], [A.Body.Neptune, '#9fb8ff', 0.9],
];

/** The stars down to a magnitude, found once per limit (the catalogue holds many thousands). */
const brightCache = new Map<string, Int32Array>();
function brightStars(mag: Float32Array, count: number, lim: number): Int32Array {
  const k = `${count}:${lim}`;
  let a = brightCache.get(k);
  if (!a) {
    const out: number[] = [];
    for (let i = 0; i < count; i++) if (mag[i] <= lim) out.push(i);
    a = Int32Array.from(out);
    brightCache.set(k, a);
  }
  return a;
}

/** Screen position on the chart for an ENU direction (null below the horizon). */
function project(v: Vec3, cx: number, cy: number, R: number): { x: number; y: number } | null {
  if (v[2] < -0.005) return null;
  const k = R / (1 + v[2]);
  return { x: cx - k * v[0], y: cy - k * v[1] };
}

const bodyEnu = (body: A.Body, d: Date, M: Mat3, obs: A.Observer): Vec3 => {
  const e = A.Equator(body, d, obs, false, true);
  const v = norm([e.vec.x, e.vec.y, e.vec.z]);
  return mat3Apply(M, v[0], v[1], v[2]);
};

/**
 * Where a sighting was: for things fixed on the sky, at the chart's moment (or its own, if it was not up then); for
 * anything else, such as a plane in an older night, where it was seen.
 */
function sightingDir(s: AppServices, x: Sighting, d: Date, M: Mat3): Vec3 | null {
  const e = s.engine;
  const at = (dir: Vec3) => {
    const v = mat3Apply(M, dir[0], dir[1], dir[2]);
    if (v[2] > 0) return v;
    const M2 = eqjToEnuMatrix(new Date(x.t), x.place);
    return mat3Apply(M2, dir[0], dir[1], dir[2]);
  };
  const sel = keyToSelection(s, x.key);
  if (sel?.kind === 'star') return at(e.starDir(sel.index));
  if (sel?.kind === 'dso') return at(e.dsos[sel.index].dir);
  if (sel?.kind === 'constellation') {
    const c = e.cons.constellations[sel.index];
    return at(raDecToVec(c.label[0], c.label[1]));
  }
  if (sel?.kind === 'body') {
    const b = A.Body[sel.id as keyof typeof A.Body];
    if (b !== undefined) {
      const obs = new A.Observer(x.place.lat, x.place.lon, x.place.height);
      const eq = A.Equator(b, d, obs, false, true);
      const v = norm([eq.vec.x, eq.vec.y, eq.vec.z]);
      const m = mat3Apply(M, v[0], v[1], v[2]);
      if (m[2] > 0) return m;
      const eq2 = A.Equator(b, new Date(x.t), obs, false, true);
      const v2 = norm([eq2.vec.x, eq2.vec.y, eq2.vec.z]);
      return mat3Apply(eqjToEnuMatrix(new Date(x.t), x.place), v2[0], v2[1], v2[2]);
    }
  }
  return x.at ? altAzToEnu(x.at[0], x.at[1]) : null;
}

/** The Moon, lit on the side facing the Sun. */
function drawMoon(g: CanvasRenderingContext2D, p: { x: number; y: number }, r: number, frac: number, towardSun: number) {
  g.save();
  g.translate(p.x, p.y);
  g.fillStyle = 'rgba(120, 124, 136, 0.35)';
  g.beginPath();
  g.arc(0, 0, r, 0, Math.PI * 2);
  g.fill();
  g.rotate(towardSun);
  const a = Math.abs(1 - 2 * frac) * r;
  g.fillStyle = '#f3eee2';
  g.beginPath();
  g.arc(0, 0, r, -Math.PI / 2, Math.PI / 2);
  if (frac < 0.5) g.ellipse(0, 0, a, r, 0, Math.PI / 2, -Math.PI / 2, true);
  else g.ellipse(0, 0, a, r, 0, Math.PI / 2, (3 * Math.PI) / 2, false);
  g.fill();
  g.restore();
}

export function drawNightChart(c: HTMLCanvasElement, s: AppServices, n: Night, o: ChartOptions) {
  const dpr = o.dpr ?? Math.min(3, window.devicePixelRatio || 1);
  const size = o.size;
  c.width = Math.round(size * dpr);
  c.height = Math.round(size * dpr);
  const g = c.getContext('2d')!;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, size, size);
  const scale = size / 320;
  const letter = Math.max(10, 11 * scale);
  // Room round the disc for the compass letters when they are drawn.
  const cx = size / 2, cy = size / 2, R = size / 2 - (o.labels ? letter * 1.6 : 1);
  const t = nightMoment(n);
  const d = new Date(t);
  const M = eqjToEnuMatrix(d, n.place);
  const obs = new A.Observer(n.place.lat, n.place.lon, n.place.height);
  const small = size < 90;

  // The sky's disc.
  const bg = g.createRadialGradient(cx, cy, 0, cx, cy, R);
  bg.addColorStop(0, '#0d1428');
  bg.addColorStop(1, '#070a14');
  g.fillStyle = bg;
  g.beginPath();
  g.arc(cx, cy, R, 0, Math.PI * 2);
  g.fill();
  g.save();
  g.beginPath();
  g.arc(cx, cy, R, 0, Math.PI * 2);
  g.clip();

  // Constellation figures, faint.
  const e = s.engine;
  if (o.lines) {
    g.strokeStyle = 'rgba(160, 180, 230, 0.2)';
    g.lineWidth = 0.8;
    for (const con of e.cons.constellations) {
      for (const line of con.lines) {
        g.beginPath();
        let pen = false;
        for (const [ra, dec] of line) {
          const v0 = raDecToVec(ra, dec);
          const p = project(mat3Apply(M, v0[0], v0[1], v0[2]), cx, cy, R);
          if (!p) {
            pen = false;
            continue;
          }
          if (pen) g.lineTo(p.x, p.y);
          else g.moveTo(p.x, p.y);
          pen = true;
        }
        g.stroke();
      }
    }
  }

  // Stars, sized by brightness and tinted by colour.
  const st = e.stars;
  const lim = o.magLimit ?? (small ? 3.6 : 5);
  for (const i of brightStars(st.mag, st.count, lim)) {
    const m = st.mag[i];
    const v = mat3Apply(M, st.dir[i * 3], st.dir[i * 3 + 1], st.dir[i * 3 + 2]);
    const p = project(v, cx, cy, R);
    if (!p) continue;
    const r = Math.max(small ? 0.45 : 0.5, (lim + 1.1 - m) * 0.42 * Math.max(0.55, scale));
    const col = (k: number) => Math.round(255 * Math.min(1, Math.pow(Math.max(0, st.color[i * 3 + k]), 1 / 2.2) * 0.35 + 0.65));
    g.fillStyle = `rgba(${col(0)}, ${col(1)}, ${col(2)}, ${Math.min(1, 0.45 + (lim - m) * 0.18)})`;
    g.beginPath();
    g.arc(p.x, p.y, r, 0, Math.PI * 2);
    g.fill();
  }

  // Planets and the Moon.
  for (const [b, color, rad] of PLANETS) {
    const p = project(bodyEnu(b, d, M, obs), cx, cy, R);
    if (!p) continue;
    g.fillStyle = color;
    g.beginPath();
    g.arc(p.x, p.y, rad * Math.max(0.7, scale) * (small ? 0.8 : 1.2), 0, Math.PI * 2);
    g.fill();
  }
  const moon = bodyEnu(A.Body.Moon, d, M, obs);
  const mp = project(moon, cx, cy, R);
  if (mp) {
    const sun = bodyEnu(A.Body.Sun, d, M, obs);
    const toward = norm([moon[0] + (sun[0] - moon[0]) * 0.02, moon[1] + (sun[1] - moon[1]) * 0.02, moon[2] + (sun[2] - moon[2]) * 0.02]);
    const q = project(toward, cx, cy, R) ?? { x: mp.x + 1, y: mp.y };
    // On the 29th, the 30th and the 1st, just a crescent (src/ui/MoonGlyph.tsx).
    const lit = A.Illumination(A.Body.Moon, d).phase_fraction;
    const frac = crescentDay(t, n.place) ? Math.max(lit, CRESCENT_LEAST) : lit;
    drawMoon(g, mp, Math.max(2.2, size * 0.022), frac, Math.atan2(q.y - mp.y, q.x - mp.x));
  }

  // What was seen: paths for things that moved, rings for things that did not (a constellation just its name).
  const labels: { x: number; y: number; text: string; r: number }[] = [];
  // Short names on the chart: "Andromeda Galaxy" rather than "M31 · Andromeda Galaxy".
  const short = (name: string) => (name.includes(' · ') ? name.slice(name.indexOf(' · ') + 3) : name);
  g.lineCap = 'round';
  for (const x of n.sightings) {
    const name = short(sightingName(s, x));
    let path: { x: number; y: number }[] = [];
    if (x.kind === 'satellite' && x.pass && x.pass.length > 1) path = x.pass.map(([alt, az]) => project(altAzToEnu(alt, az), cx, cy, R)).filter((p): p is { x: number; y: number } => !!p);
    else if (x.kind === 'meteor' && x.path) path = x.path.map(([alt, az]) => project(altAzToEnu(alt, az), cx, cy, R)).filter((p): p is { x: number; y: number } => !!p);
    if (path.length > 1) {
      g.strokeStyle = x.kind === 'meteor' ? 'rgba(239, 207, 143, 0.95)' : 'rgba(170, 225, 255, 0.9)';
      g.lineWidth = small ? 1 : Math.max(1.4, 2 * scale);
      g.beginPath();
      path.forEach((p, k) => (k ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
      g.stroke();
      // An arrowhead shows which way it went.
      if (!small) {
        const a = path[path.length - 2], b = path[path.length - 1];
        const ang = Math.atan2(b.y - a.y, b.x - a.x), h = 6 * Math.max(0.8, scale);
        g.beginPath();
        g.moveTo(b.x, b.y);
        g.lineTo(b.x - h * Math.cos(ang - 0.45), b.y - h * Math.sin(ang - 0.45));
        g.moveTo(b.x, b.y);
        g.lineTo(b.x - h * Math.cos(ang + 0.45), b.y - h * Math.sin(ang + 0.45));
        g.stroke();
      }
      labels.push({ ...path[path.length >> 1], text: name, r: 0 });
      continue;
    }
    const v = sightingDir(s, x, d, M);
    const p = v && project(v, cx, cy, R);
    if (!p) continue;
    if (x.kind === 'constellation') {
      labels.push({ ...p, text: name, r: 0 });
      continue;
    }
    g.strokeStyle = GOLD;
    g.lineWidth = small ? 1 : Math.max(1.2, 1.6 * scale);
    g.beginPath();
    g.arc(p.x, p.y, small ? 3.2 : Math.max(6, 10 * scale), 0, Math.PI * 2);
    g.stroke();
    labels.push({ ...p, text: name, r: small ? 3.2 : Math.max(6, 10 * scale) });
  }
  g.restore();

  // The horizon, and the compass points (east on the left, as overhead).
  g.strokeStyle = 'rgba(255, 255, 255, 0.16)';
  g.lineWidth = 1;
  g.beginPath();
  g.arc(cx, cy, R, 0, Math.PI * 2);
  g.stroke();
  if (o.labels) {
    g.font = `600 ${letter}px -apple-system, BlinkMacSystemFont, system-ui, sans-serif`;
    g.fillStyle = 'rgba(237, 240, 247, 0.5)';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const gap = letter * 0.85;
    g.fillText('N', cx, cy - R - gap);
    g.fillText('S', cx, cy + R + gap);
    g.fillText('E', cx - R - gap, cy);
    g.fillText('W', cx + R + gap, cy);
    // Names beside their rings: to the right, left, above or below, whichever stays inside the disc and
    // clear of the other names and rings; a name with no clear place is left out (the list has it).
    g.font = `600 ${Math.max(11, 12.5 * scale)}px -apple-system, BlinkMacSystemFont, system-ui, sans-serif`;
    g.textBaseline = 'middle';
    g.textAlign = 'left';
    g.shadowColor = 'rgba(0, 0, 0, 0.8)';
    g.shadowBlur = 4;
    g.fillStyle = GOLD;
    const h = 14 * Math.max(1, scale), pad = 2 * Math.max(1, scale);
    const placed = labels.filter((l) => l.r).map((l) => ({ x0: l.x - l.r, y0: l.y - l.r, x1: l.x + l.r, y1: l.y + l.r }));
    const inside = (x0: number, y0: number, w: number) =>
      [[x0, y0], [x0 + w, y0], [x0, y0 + h], [x0 + w, y0 + h]].every(([x, y]) => (x - cx) ** 2 + (y - cy) ** 2 < (R - 2) ** 2);
    for (const l of labels.slice(0, 12)) {
      const w = g.measureText(l.text).width;
      const off = l.r ? l.r + 5 * Math.max(1, scale) : 0;
      const spots: [number, number][] = l.r
        ? [[l.x + off, l.y - h / 2], [l.x - off - w, l.y - h / 2], [l.x - w / 2, l.y - off - h], [l.x - w / 2, l.y + off]]
        : [[l.x - w / 2, l.y - h / 2], [l.x + 6, l.y - h / 2], [l.x - w - 6, l.y - h / 2]];
      const spot = spots.find(([x0, y0]) => inside(x0, y0, w) && !placed.some((b) => x0 - pad < b.x1 && x0 + w + pad > b.x0 && y0 - pad < b.y1 && y0 + h + pad > b.y0));
      if (!spot) continue;
      placed.push({ x0: spot[0], y0: spot[1], x1: spot[0] + w, y1: spot[1] + h });
      g.fillText(l.text, spot[0], spot[1] + h / 2);
    }
    g.shadowBlur = 0;
  }
}
